/* CARRYING THE QUEUE'S PAGE INTO A VIDEO AND BACK OUT AGAIN.
 * ===========================================================================
 *
 * Testing, 21 Sep, point 5: "From the working screen, if I click back button,
 * I should go back to the same page I was before clicking the specific video.
 * Currently, each time I am landing to the first page."
 *
 * The first attempt at this put the page number into the queue's own URL with
 * `useSearchParams`, on the reasoning that the address bar is the one piece of
 * state the browser preserves across navigation for free. That was necessary
 * and not sufficient, because the tool's own "← Back" button is not the
 * browser's:
 *
 *     <Link to="/">          navigates to the path "/" with NO query string
 *
 * So the page was faithfully recorded at /?page=3, and then thrown away by the
 * one control whose entire job is to go back. Putting state in the URL only
 * helps if the thing that navigates carries the URL.
 *
 * Hence these two functions, which are each other's inverse:
 *
 *     queue    /?page=3   --toVideo("/job/229")-->   /job/229?page=3
 *     video    /job/229?page=3   --backToQueue()-->  /?page=3
 *
 * WHY THE WHOLE SEARCH STRING AND NOT JUST `page`. The queue owns its query
 * string; anything it decides to keep there (a future `q=`, a sort order)
 * should come back with the reviewer without this file needing to learn about
 * it. The video screens read none of these parameters — they route on the path
 * alone — so carrying them through is inert until the queue uses them.
 *
 * WHY NOT ROUTER STATE. `navigate(to, { state })` is the idiomatic answer and
 * it does not survive a reload or a pasted link: a reviewer who refreshes the
 * workspace, or opens it from a message, would land back on page 1 — which is
 * the exact complaint. The query string survives both.
 */

/** The queue link for a video, carrying wherever the reviewer is in the queue.
 *
 *  `search` is the queue's own `location.search`, leading "?" and all, which is
 *  what both `useLocation` and `useSearchParams` hand you. Empty means page 1
 *  and earns no query string — a clean /job/229 for the common case. */
export function toVideo(path: string, search: string): string {
  const q = normalise(search);
  return q ? `${path}?${q}` : path;
}

/** Where "← Back" should go from a video screen: the queue, on the page the
 *  reviewer left it on.
 *
 *  Returns a `To` object rather than a string because react-router treats a
 *  string `to` as a whole path and re-parses it; handing it the parts avoids
 *  one round of encode/decode over values we did not encode. */
export function backToQueue(search: string): { pathname: string; search: string } {
  return { pathname: "/", search: normalise(search) };
}

/** Strip the leading "?" and any empty-string edge cases, so the two functions
 *  above can each assume a bare `a=1&b=2` or "".
 *
 *  A lone "?" is what some browsers leave behind after the last parameter is
 *  deleted; treating it as a query string would produce "/job/229?" and, worse,
 *  a Back target of "/?" that differs from "/" for no visible reason. */
function normalise(search: string): string {
  const q = (search ?? "").replace(/^\?/, "");
  return q;
}
